import React, {Fragment, useState} from 'react';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {
  Classes,
  Menu,
  MenuItem,
  Button,
  Intent,
  Dialog,
  Drawer,
  Popover,
  Position,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import {MultiSelect} from '@blueprintjs/select';
import {Label, TextField} from 'worksource-ui';
import {useQuery, useMutation} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';

import {useUIActions} from 'utils/ui-context';
import {showSuccess, showWSErrors, showGQLError} from 'utils/toaster';

const USER_FRAGMENT = gql`
  fragment UsersEditDialogUser on User {
    id,
    fullName,
    email,
    groups {
      id,
      name,
    },
    guides {
      id,
      name,
    },
    tags,
    role,
    lastLoggedInAt,
    latestInvitation {
      isReminder,
      sentAt,
      openedAt,
      clickedCtaLinkAt,
      clickedCtaLinkAt,
    }
  }
`;

const FETCH_USER = gql`
  query UsersEditDialogEditFetch($clientId: ID!, $userId: ID!) {
    users(clientId: $clientId, id: $userId) {
      ...UsersEditDialogUser,
    },
    tags(clientId: $clientId),
    groups(clientId: $clientId) {
      name,
      id,
    },
  }
  ${USER_FRAGMENT}
`;

const FETCH_CREATE_DATA = gql`
  query UsersEditDialogCreateFetch($clientId: ID!) {
    tags(clientId: $clientId),
    groups(clientId: $clientId) {
      id,
      name,
    },
  }
`;

const UPDATE_USER = gql`
  mutation UsersEditDialogUpdate(
    $clientId: ID!,
    $userId: ID!,
    $fullName: String,
    $email: String,
    $role: ClientRoleRole,
    $groupIds: [ID!],
    $tags: [String!],
  ) {
    updateUser(
      clientId: $clientId,
      userId: $userId,
      attributes: {
        fullName: $fullName,
        email: $email,
        role: $role,
        groupIds: $groupIds,
        tags: $tags,
      },
    ) {
      user {
        ...UsersEditDialogUser,
      },
      errors {
        title,
        message,
      },
    }
  }
  ${USER_FRAGMENT}
`;

const CREATE_USER = gql`
  mutation UsersEditDialogCreate(
    $clientId: ID!,
    $fullName: String,
    $email: String,
    $role: ClientRoleRole,
    $groupIds: [ID!],
    $tags: [String!],
  ) {
    createUser(
      clientId: $clientId,
      attributes: {
        fullName: $fullName,
        email: $email,
        role: $role,
        groupIds: $groupIds,
        tags: $tags,
      },
    ) {
      user {
        ...UsersEditDialogUser,
      },
      errors {
        title,
        message,
      },
    }
  }
  ${USER_FRAGMENT}
`;

const DELETE_USER = gql`
  mutation UsersEditDialogDelete($clientId: ID!, $userId: ID!) {
    deleteUser(clientId: $clientId, userId: $userId) {
      success,
      errors {
        title,
        message,
      }
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation UsersEditDialogResetPassword($clientId: ID!, $userId: ID!) {
    resetPassword(clientId: $clientId, userId: $userId) {
      success,
      errors {
        title,
        message,
      }
    }
  }
`;

const RESEND_INVITATION = gql`
  mutation UsersEditDialogResendWelcome($clientId: ID!, $userId: ID!) {
    resendInvitation(clientId: $clientId, userId: $userId) {
      success,
      errors {
        title,
        message,
      }
    }
  }
`;

const renderCreateTag = (query, active, handleClick) => {
  return (
    <MenuItem
      icon="add"
      text={`Create ${query}`}
      active={active}
      onClick={handleClick}
      shouldDismissPopover={false}
    />
  );
};

const renderTag = (tag, {modifiers, handleClick}) => {
  return (
    <MenuItem
      active={modifiers.active}
      key={tag}
      onClick={handleClick}
      text={tag}
      shouldDismissPopover={false}
    />
  );
};

const renderGroup = (group, {modifiers, handleClick}) => {
  return (
    <MenuItem
      active={modifiers.active}
      key={group.id}
      onClick={handleClick}
      text={group.name}
      shouldDismissPopover={false}
    />
  );
};

const UsersEditDialog = (props) => {
  const isCreating = !props.userId;

  ////
  // State/effect Hooks
  //
  const {clearDialog} = useUIActions();
  const history = useHistory();
  const [tagsQuery, setTagsQuery] = useState('');
  const [groupsQuery, setGroupsQuery] = useState('');
  const [hideGuides, setHideGuides] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    role: 'user',
    tags: [],
    groups: [],
  });

  ////
  // GraphQL queries
  //
  const {loading: loadingFetch, data} = useQuery(isCreating ? FETCH_CREATE_DATA : FETCH_USER, {
    variables: {
      userId: props.userId,
      clientId: props.clientId,
    },
    onCompleted: ({users}) => {
      if (!users) return;
      setForm({...users[0]});
    },
    onError: error => showGQLError({error}),
  });
  const [deleteUser, {loading: loadingDelete}] = useMutation(DELETE_USER, {
    onCompleted: ({deleteUser: {errors}}) => {
      if (errors.length > 0) {
        showWSErrors({errors});
      } else {
        showSuccess({message: 'User deleted successfully'});
        clearDialog();
      }
    },
    onError: error => showGQLError({error}),
    refetchQueries: ['FetchAllUsers'],
  });
  const [resetPassword, {loading: loadingResetPassword}] = useMutation(RESET_PASSWORD, {
    onCompleted: ({resetPassword: {errors}}) => {
      if (errors.length > 0) {
        showWSErrors({errors});
      } else {
        showSuccess({message: 'Email sent successfully'});
      }
    },
    onError: error => showGQLError({error}),
  });
  const [_resendInvitation, {loading: loadingResend}] = useMutation(RESEND_INVITATION, {
    onCompleted: ({resendInvitation: {errors}}) => {
      if (errors.length > 0) {
        showWSErrors({errors});
      } else {
        showSuccess({message: 'Email sent successfully'});
      }
    },
    onError: error => showGQLError({error}),
  });
  const [saveUser, {loading: loadingSave}] = useMutation(isCreating ? CREATE_USER : UPDATE_USER, {
    onCompleted: ({updateUser, createUser}) => {
      const {errors} = (updateUser || createUser);
      if (errors.length > 0) {
        showWSErrors({errors});
        return;
      }

      showSuccess({message: 'User updated successfully'});
      clearDialog();
    },
    onError: error => showGQLError({error}),
    refetchQueries: isCreating ? ['FetchAllUsers'] : [],
  });

  ////
  // Events
  //
  const save = () => saveUser({variables: {
    userId: props.userId,
    clientId: props.clientId,
    ...form,
    groupIds: form.groups.map(g => g.id),
  }});

  const destroy = () => deleteUser({variables: {
    userId: props.userId,
    clientId: props.clientId,
  }});

  const sendReset = () => resetPassword({variables: {
    userId: props.userId,
    clientId: props.clientId,
  }});

  const resendInvitation = () => _resendInvitation({variables: {
    userId: props.userId,
    clientId: props.clientId,
  }});

  ////
  // Render
  //
  const {tags, groups} = data || {tags: [], groups: []};
  const loading = loadingSave || loadingFetch || loadingDelete || loadingResetPassword;

  return (
    <Fragment>
      <Dialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        icon="trash"
        title="Confirm delete"
      >
        <div className={Classes.DIALOG_BODY}>
          Are you sure you wish to delete this user?
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              onClick={() => setShowConfirmDelete(false)}
              disabled={loading}
              text="Cancel"
            />
            <Button
              onClick={destroy}
              disabled={loading}
              loading={loadingDelete}
              intent={Intent.DANGER}
              text="Delete"
            />
          </div>
        </div>
      </Dialog>
      <Drawer
        isOpen={props.isOpen}
        onClose={clearDialog}
        icon="user"
        title="Edit user"
      >
        <div className={Classes.DRAWER_BODY}>
          <div className={Classes.DIALOG_BODY}>
            <h2>Basic information</h2>
            <Label required text="E-mail Address">
              <TextField
                fluid
                placeholder="user@example.com"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                disabled={loading}
              />
            </Label>

            <Label required text="Full Name">
              <TextField
                fluid
                placeholder="Point Nameston"
                value={form.fullName}
                onChange={e => setForm({...form, fullName: e.target.value})}
                disabled={loading}
              />
            </Label>
            <Label text="Role">
              <Popover
                position={Position.BOTTOM_LEFT}
                fill={false}
                content={<Menu>
                  <MenuItem
                    icon="badge"
                    text="Admin"
                    onClick={() => setForm({...form, role: 'admin'})}
                  />
                  <MenuItem
                    icon="user"
                    text="User"
                    onClick={() => setForm({...form, role: 'user'})}
                  />
                </Menu>}
              >
                <Button
                  icon={form.role === 'user' ? 'user' : 'badge'}
                  text={`${form.role.charAt(0).toUpperCase()}${form.role.slice(1)}`}
                  className="users-edit--role-button"
                  disabled={loading}
                />
              </Popover>
            </Label>
            <Label text="Tags">
              <MultiSelect
                query={tagsQuery}
                onQueryChange={q => setTagsQuery(q)}
                placeholder="Select tags"
                resetOnSelect
                popoverProps={{minimal: true}}
                createNewItemFromQuery={tag => tag}
                createNewItemRenderer={renderCreateTag}
                items={tags}
                itemRenderer={renderTag}
                selectedItems={form.tags}
                itemPredicate={(query, tag) => {
                  return tag.toLowerCase().includes(query.toLowerCase());
                }}
                onItemSelect={(tag) => {
                  setTagsQuery('');
                  setForm({...form, tags: [...form.tags, tag]});
                }}
                tagInputProps={{
                  disabled: loading,
                  tagProps: () => ({minimal: true}),
                  onRemove: (tag) => {
                    setForm({...form, tags: form.tags.filter(t => t !== tag)});
                  },
                }}
                tagRenderer={tag => tag}
              />
            </Label>
            <Label text="Groups">
              <MultiSelect
                query={groupsQuery}
                onQueryChange={q => setGroupsQuery(q)}
                placeholder={(() => {
                  if (groups.length === 0 && !loading) {
                    return 'Your organization doesn\'t have any groups yet.';
                  }

                  return 'Select groups';
                })()}
                resetOnSelect
                popoverProps={{minimal: true}}
                items={groups}
                itemRenderer={renderGroup}
                selectedItems={form.groups}
                itemPredicate={(query, group) => {
                  return group.name.toLowerCase().includes(query.toLowerCase());
                }}
                onItemSelect={(group) => {
                  setGroupsQuery('');
                  setHideGuides(true);
                  setForm({...form, groups: [...form.groups, group]});
                }}
                tagInputProps={{
                  disabled: loading || groups.length === 0,
                  tagProps: () => ({minimal: true}),
                  onRemove: (_, index) => {
                    setGroupsQuery('');
                    setHideGuides(true);
                    setForm({...form, groups: form.groups.filter((group, i) => i !== index)});
                  },
                }}
                tagRenderer={group => group.name}
              />
            </Label>

            {isCreating && (
              <p>
                Clicking save will send a welcome email to the new user asking
                them to create a new password and log in for the first time.
              </p>
            )}

            {!isCreating && (
              <div className="users-edit--guides-list">
                <h2>Guides</h2>
                {(form.guides && !hideGuides) && (
                  <Fragment>
                    {form.guides.length > 0 && (
                      <p>
                        This user is able to see the following guides:
                      </p>
                    )}
                    {form.guides.length === 0 && (
                      <p>
                        This user cannot see any guides. Try adding them to a group
                        or guide.
                      </p>
                    )}
                    {form.guides.map((guide) => {
                      return (
                        <Tag
                          key={guide.id}
                          onClick={() => {
                            clearDialog();
                            history.push(`/clients/${props.clientId}/guides/${guide.id}/edit`);
                          }}
                          icon="book"
                          large
                          minimal
                          interactive
                          round
                        >
                          {guide.name}
                        </Tag>
                      );
                    })}
                  </Fragment>
                )}
                {hideGuides && (
                  <p>
                    You have modified this user's memberships. A visible guide
                    list cannot be shown until the changes have been saved.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={Classes.DRAWER_FOOTER}>
          <div className="bp3-dialog-footer-actions">
            {(!isCreating && form.lastLoggedInAt) && (
              <Button
                onClick={sendReset}
                disabled={loading}
                loading={loadingResetPassword}
                text="Reset Password"
              />
            )}
            {(!isCreating && !form.lastLoggedInAt && form.latestInvitation) && (
              <Tooltip
                content={`Last invitation sent ${moment(form.latestInvitation.sentAt).fromNow()}`}
              >
                <Button
                  onClick={resendInvitation}
                  disabled={loadingResend}
                  loading={loadingResend}
                  text="Resend Welcome Email"
                />
              </Tooltip>
            )}
            <Button
              onClick={clearDialog}
              disabled={loading}
              text="Cancel"
            />
            {!isCreating && (
              <Button
                onClick={() => setShowConfirmDelete(true)}
                disabled={loading}
                intent={Intent.DANGER}
                text="Delete"
              />
            )}
            <Button
              onClick={save}
              intent={Intent.PRIMARY}
              disabled={loading}
              loading={loadingSave}
              text={isCreating ? 'Create' : 'Save'}
            />
          </div>
        </div>
      </Drawer>
    </Fragment>
  );
};


export default UsersEditDialog;
